import type { AxiosResponse } from 'axios'
import { call, select, takeEvery, takeLatest } from 'redux-saga/effects'

import { getActiveCompanyId, getErrorMessage, parseApiErrorMessage } from '@helpers'

import actions from './actions'
import * as api from './api'
import type {
  RemoveOnboardingDocumentPayload,
  UploadOnboardingDocumentsPayload,
  UploadOnboardingDocumentsResults,
} from './types'

function* fetchOnboardingDocumentsSaga({ meta: { resolve, reject } }: AsyncSagaAction<void>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<BackendCompanyDocument[]> = yield call(api.fetchOnboardingDocuments, companyId)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* uploadOnboardingDocumentsSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<UploadOnboardingDocumentsPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const results: UploadOnboardingDocumentsResults = { rejected: [], uploaded: [] }

    //* the endpoint takes one document per request, so the files are uploaded one after the other and
    //* the per file errors are collected for the caller instead of failing the whole batch
    //! keep this sequential: the backend appends the new document's id to the company's onboarding
    //! data with a read-modify-write, so parallel requests would drop each other's ids
    for (const [fileIndex, file] of payload.files.entries()) {
      try {
        const response: AxiosResponse<BackendCompanyDocument> = yield call(
          api.uploadOnboardingDocument,
          companyId,
          file,
          function onUploadProgress({ loaded, total }) {
            //* total is missing while the request is still being prepared
            payload.onProgress?.(fileIndex, total ? Math.round((loaded / total) * 100) : 0)
          }
        )
        results.uploaded.push(response.data)
      } catch (error) {
        results.rejected.push({ filename: file.name, message: parseApiErrorMessage(error) ?? '' })
      }
    }

    yield call(resolve, results)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* removeOnboardingDocumentSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<RemoveOnboardingDocumentPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    yield call(api.removeOnboardingDocument, companyId, payload)
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// watcher Saga
export default function* onboardingDocumentsSaga() {
  yield takeLatest(actions.fetchOnboardingDocuments.REQUEST, fetchOnboardingDocumentsSaga)
  //* a submit deletes every document the user removed from the list, so the requests must not cancel
  //* each other - a cancelled saga never settles its promise and would hang the submit
  yield takeEvery(actions.removeOnboardingDocument.REQUEST, removeOnboardingDocumentSaga)
  yield takeEvery(actions.uploadOnboardingDocuments.REQUEST, uploadOnboardingDocumentsSaga)
}
